import React, { useEffect, useState } from "react";
import {
  Box, Button, Flex, Table, Tbody, Td, Th, Thead, Tr, Text, useToast, Spinner,
  HStack, Input, Heading, SimpleGrid,
} from "@chakra-ui/react";
import {
  getDevProviders, addDevProvider, removeDevProvider,
  getDevCustomers, addDevCustomer, removeDevCustomer, refreshCatalog,
} from "../../utils/apis/agentApi";

/** Agent dev allowlist: dev customers get ONLY dev providers recommended, for
 *  safe testing against live data. Plus a force-catalog-refresh utility. */
const DevData = () => {
  const [providers, setProviders] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pid, setPid] = useState(""); const [pname, setPname] = useState("");
  const [phone, setPhone] = useState(""); const [note, setNote] = useState("");
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    const [p, c] = await Promise.all([getDevProviders(), getDevCustomers()]);
    if (p.ok && p.data) setProviders(p.data.providers || []);
    if (c.ok && c.data) setCustomers(c.data.customers || []);
    if (!p.ok || !c.ok) toast({ title: p.status === 502 || c.status === 502 ? "Agent unreachable" : "Failed to load",
      status: "error", duration: 3000, isClosable: true });
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const act = async (fn: () => Promise<any>, ok: string) => {
    const res = await fn();
    if (res.ok) { toast({ title: ok, status: "success", duration: 1500 }); load(); }
    else toast({ title: "Failed", status: "error", duration: 3000 });
  };

  const doRefreshCatalog = async () => {
    const res = await refreshCatalog();
    if (res.ok) toast({ title: `Catalog refreshed (${res.data?.services ?? "?"} services)`, status: "success", duration: 3000 });
    else toast({ title: "Refresh failed", status: "error", duration: 3000 });
  };

  return (
    <Box p={5}>
      <Flex justifyContent="space-between" mb={4}>
        <Heading size="md">Dev allowlist</Heading>
        <HStack>
          <Button variant="outline" colorScheme="purple" onClick={doRefreshCatalog}>Refresh catalog</Button>
          <Button colorScheme="purple" backgroundColor="#88077b" onClick={load}>Refresh</Button>
        </HStack>
      </Flex>
      <Box mb={4} p={3} borderWidth="1px" borderRadius="md" backgroundColor="admin.subtle">
        <Text fontSize="sm" color="admin.muted">
          <b>What this is:</b> a safe test lane. Any phone listed under <b>Dev customers</b>
          {" "}will only ever be recommended the providers listed under <b>Dev providers</b>,
          so you can test the live agent end to end without messaging real workers.
        </Text>
        <Text fontSize="sm" color="admin.muted" mt={2}>
          <b>To add a provider:</b> open <b>Service Providers</b> in the sidebar, click the one
          you want, and copy the <b>id</b> from the page URL (the long code after
          {" "}<code>/provider/</code>). Paste it as <code>provider_id</code> below.
          {" "}<b>To add a customer:</b> just enter their WhatsApp number in full
          international form, e.g. <code>+2348012345678</code>.
        </Text>
      </Box>

      {loading ? <Spinner size="xl" /> : (
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          {/* Dev providers */}
          <Box>
            <Heading size="sm" mb={2}>Dev providers</Heading>
            <HStack mb={3}>
              <Input size="sm" placeholder="provider_id" value={pid} onChange={(e) => setPid(e.target.value)} />
              <Input size="sm" placeholder="name (optional)" value={pname} onChange={(e) => setPname(e.target.value)} />
              <Button size="sm" colorScheme="purple" backgroundColor="#88077b" isDisabled={!pid.trim()}
                onClick={() => act(() => addDevProvider(pid.trim(), pname.trim() || undefined), "Added").then(() => { setPid(""); setPname(""); })}>Add</Button>
            </HStack>
            <Table variant="striped" size="sm">
              <Thead backgroundColor="#88077b"><Tr>
                <Th color="white">Provider</Th><Th color="white">Name</Th><Th color="white"></Th>
              </Tr></Thead>
              <Tbody>
                {providers.map((p, i) => (
                  <Tr key={p.provider_id} backgroundColor={i % 2 === 0 ? "admin.surface" : "admin.selected"}>
                    <Td>{p.provider_id}</Td><Td>{p.name || "-"}</Td>
                    <Td><Button size="xs" colorScheme="red" onClick={() => act(() => removeDevProvider(p.provider_id), "Removed")}>Remove</Button></Td>
                  </Tr>
                ))}
                {providers.length === 0 && <Tr><Td colSpan={3}><Text color="admin.muted">None.</Text></Td></Tr>}
              </Tbody>
            </Table>
          </Box>

          {/* Dev customers */}
          <Box>
            <Heading size="sm" mb={2}>Dev customers</Heading>
            <HStack mb={3}>
              <Input size="sm" placeholder="phone (+234…)" value={phone} onChange={(e) => setPhone(e.target.value)} />
              <Input size="sm" placeholder="note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
              <Button size="sm" colorScheme="purple" backgroundColor="#88077b" isDisabled={!phone.trim()}
                onClick={() => act(() => addDevCustomer(phone.trim(), note.trim() || undefined), "Added").then(() => { setPhone(""); setNote(""); })}>Add</Button>
            </HStack>
            <Table variant="striped" size="sm">
              <Thead backgroundColor="#88077b"><Tr>
                <Th color="white">Phone</Th><Th color="white">Note</Th><Th color="white"></Th>
              </Tr></Thead>
              <Tbody>
                {customers.map((c, i) => (
                  <Tr key={c.phone} backgroundColor={i % 2 === 0 ? "admin.surface" : "admin.selected"}>
                    <Td>{c.phone}</Td><Td>{c.note || "-"}</Td>
                    <Td><Button size="xs" colorScheme="red" onClick={() => act(() => removeDevCustomer(c.phone), "Removed")}>Remove</Button></Td>
                  </Tr>
                ))}
                {customers.length === 0 && <Tr><Td colSpan={3}><Text color="admin.muted">None.</Text></Td></Tr>}
              </Tbody>
            </Table>
          </Box>
        </SimpleGrid>
      )}
    </Box>
  );
};
export default DevData;
